<template>
  <Teleport to="body">
    <div
        tabindex="-1"
        v-show="isShow"
        class="confirm-phone-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-phone-title"
    >
      <div class="confirm-phone-modal__backdrop"></div>
      <div class="confirm-phone-modal__panel">
        <div class="confirm-phone-modal__header">
          <div class="confirm-phone-modal__eyebrow">Бронирование</div>
          <h3 id="confirm-phone-title" class="confirm-phone-modal__title">
            Подтверждение телефона
          </h3>
          <button
              type="button"
              @click="close()"
              class="confirm-phone-modal__close"
              aria-label="Закрыть"
              data-modal-hide="default-modal"
          >
            <svg class="confirm-phone-modal__close-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 14 14">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
          </button>
        </div>

        <div class="confirm-phone-modal__body">
          <div class="confirm-phone-modal__intro" v-if="!hasRequestedCall">
            <div class="confirm-phone-modal__step">Шаг 1</div>
            <div class="confirm-phone-modal__text">
              Запросите автоматический звонок
            </div>
            <div class="confirm-phone-modal__hint">
              Мы позвоним на номер из формы бронирования. Отвечать на звонок не нужно.
            </div>
            <div class="confirm-phone-modal__phone">
              {{ formattedPhone || 'Телефон из формы бронирования' }}
            </div>
          </div>

          <div class="confirm-phone-modal__confirm" v-if="hasRequestedCall">
            <div class="confirm-phone-modal__step">Шаг 2</div>
            <div class="confirm-phone-modal__text">
              Введите последние 4 цифры входящего номера
            </div>
            <div class="confirm-phone-modal__hint">
              После ввода цифр нажмите кнопку подтверждения, чтобы завершить бронирование.
            </div>
          </div>

          <input
              ref="codeInputEl"
              type="text"
              class="confirm-phone-modal__code"
              v-show="hasRequestedCall"
              inputmode="numeric"
              autocomplete="one-time-code"
          />

          <button
              v-if="hasRequestedCall"
              type="button"
              :disabled="!canConfirm"
              @click="confirmRequest"
              class="confirm-phone-modal__submit"
              :class="{'confirm-phone-modal__submit--disabled': !canConfirm}"
          >
            Подтвердить
          </button>

          <button
              v-if="canRequest"
              type="button"
              @click="sendRequest"
              class="confirm-phone-modal__request"
              :class="{'confirm-phone-modal__request--secondary': hasRequestedCall}"
          >
            {{ hasRequestedCall ? 'Запросить звонок повторно' : 'Запросить звонок' }}
          </button>

          <div v-show="isSendRequest" class="confirm-phone-modal__timer">
            Повторно запросить звонок можно через: <span>{{ timeText }}</span>
          </div>

          <pre v-if="!!errorsText" class="confirm-phone-modal__errors">{{errorsText}}</pre>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import IMask from 'imask';
import {computed, ref, onMounted, defineProps, defineExpose} from 'vue';

const props = defineProps([
  'phone'
]);

const codeInputEl = ref(null);
const isShow = ref(false);
const code = ref(null);
const canRequest = ref(true);
const canConfirm = ref(false);
const hasRequestedCall = ref(false);
const isSendRequest = ref(false);
const timeText = ref(null);

let errorsText = ref('');

const seconds = 60;
let interval = null;
let timer = null;
let codeMask = null;

const getPhoneValue = () => {
  if (props.phone && typeof props.phone === 'object' && 'value' in props.phone) {
    return props.phone.value;
  }

  return props.phone;
};

const formattedPhone = computed(() => {
  const phone = String(getPhoneValue() || '').trim();

  if (!phone) {
    return '';
  }

  return phone.startsWith('+') ? phone : `+${phone}`;
});

const sendRequest = async () => {
  if (isSendRequest.value === true) return;

  resetCodeState();

  let result = await (await fetch('/order/contact-request', {
    method: 'post',
    body: getPhoneValue(),
    headers: {
      'Content-Type': 'application/json'
    }
  })).json();

  if (result.errors !== undefined) {
    errorsText.value += Object.values(result.errors).join('\n');
  }
  if (result.message !== undefined && result.message) {
    errorsText.value += result.message + '\n';
  }
  if (result.success === false) return;

  canRequest.value = false;
  hasRequestedCall.value = true;
  isSendRequest.value = true;

  if (interval) clearInterval(interval);
  if (timer) clearTimeout(timer);

  timeText.value = seconds;
  interval = setInterval(() => {
    timeText.value -= 1;
  }, 1000);
  timer = setTimeout(() => {
    timeText.value = null;
    canRequest.value = true;
    isSendRequest.value = false;
  }, 1000 * seconds)
};

const confirmRequest = async () => {
  if (!canConfirm.value) return;

  errorsText.value = '';

  let result = await (await fetch('/order/contact-confirm', {
    method: 'post',
    body: JSON.stringify({
      phone: getPhoneValue(),
      code: code.value
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })).json();

  if (result.errors !== undefined) {
    errorsText.value += Object.values(result.errors).join('\n');
  }
  if (result.message !== undefined && result.message) {
    errorsText.value += result.message + '\n';
  }
  if (result.success === true) {
    resetCallState();
    close();
  }
}

const resetCodeState = () => {
  errorsText.value = '';
  code.value = null;
  canConfirm.value = false;

  if (codeMask) {
    codeMask.value = '';
  }
};

const resetCallState = () => {
  resetCodeState();
  hasRequestedCall.value = false;
  canRequest.value = true;
  isSendRequest.value = false;
  timeText.value = null;

  if (interval) clearInterval(interval);
  if (timer) clearTimeout(timer);

  interval = null;
  timer = null;
};

const open = () => {
  isShow.value = true;
  resetCodeState();
};
const close = () => {
  isShow.value = false;
  resetCodeState();
}

defineExpose({
  open,
  close
})

onMounted(() => {
  codeMask = IMask(
      codeInputEl.value,
      {
        mask: 'X X X X',
        definitions: {
          X: {
            mask: Number,
            placeholderChar: '_',
          },
        },
        lazy: false,
        overwrite: 'shift',
      }
  )
  codeMask.on('accept', () => {
    code.value = codeMask.unmaskedValue;
    canConfirm.value = codeMask.unmaskedValue.length === 4;
  });
  codeMask.on('complete', () => {
    code.value = codeMask.unmaskedValue;
    canConfirm.value = true;
  });
});

</script>

<style scoped>
.confirm-phone-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  width: 100vw;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px;
}

.confirm-phone-modal__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(14, 12, 10, 0.72);
  backdrop-filter: blur(6px);
}

.confirm-phone-modal__panel {
  position: relative;
  z-index: 1;
  width: min(100%, 520px);
  border: 1px solid rgba(161, 128, 76, 0.18);
  border-radius: 28px;
  background: rgba(255, 253, 249, 0.97);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.32);
  color: #2d2924;
  overflow: hidden;
}

.confirm-phone-modal__header {
  position: relative;
  padding: 28px 64px 0 28px;
}

.confirm-phone-modal__eyebrow {
  color: #8d6b37;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.confirm-phone-modal__title {
  margin: 8px 0 0;
  color: #2d2924;
  font-size: 30px;
  font-weight: 300;
  line-height: 1.15;
}

.confirm-phone-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(161, 128, 76, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: #5d5449;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.confirm-phone-modal__close:hover {
  background: #f7efe1;
  color: #111111;
  transform: translateY(-1px);
}

.confirm-phone-modal__close-icon {
  width: 14px;
  height: 14px;
}

.confirm-phone-modal__body {
  padding: 26px 28px 30px;
  text-align: center;
}

.confirm-phone-modal__intro,
.confirm-phone-modal__confirm {
  max-width: 380px;
  margin: 0 auto;
}

.confirm-phone-modal__step {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #f7efe1;
  color: #8d6b37;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  padding: 0 14px;
  text-transform: uppercase;
}

.confirm-phone-modal__phone {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  margin-top: 18px;
  border: 1px solid rgba(161, 128, 76, 0.2);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.74);
  padding: 0 18px;
  color: #4b4339;
  font-size: 16px;
  font-weight: 600;
}

.confirm-phone-modal__text {
  margin-top: 14px;
  color: #2d2924;
  font-size: 18px;
  font-weight: 500;
}

.confirm-phone-modal__hint {
  max-width: 360px;
  margin: 10px auto 0;
  color: #6c6459;
  font-size: 14px;
  line-height: 1.55;
}

.confirm-phone-modal__code {
  width: 170px;
  min-height: 64px;
  margin-top: 8px;
  border: 1px solid #d8cfbf;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  color: #201c18;
  font-size: 34px;
  letter-spacing: 0;
  line-height: 1;
  outline: none;
  padding: 10px 14px;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.confirm-phone-modal__code:focus {
  border-color: #b98a43;
  box-shadow: 0 0 0 4px rgba(185, 138, 67, 0.14);
}

.confirm-phone-modal__submit {
  display: inline-flex;
  width: 100%;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
  border: 0;
  border-radius: 999px;
  background: #171513;
  color: #f4ead5;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.confirm-phone-modal__submit:hover {
  background: #000000;
  transform: translateY(-1px);
}

.confirm-phone-modal__submit--disabled,
.confirm-phone-modal__submit--disabled:hover {
  background: #8e877c;
  color: rgba(255, 255, 255, 0.88);
  cursor: not-allowed;
  transform: none;
}

.confirm-phone-modal__request {
  display: inline-flex;
  width: 100%;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  border: 0;
  border-radius: 999px;
  background: #f4ead5;
  color: #111111;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.confirm-phone-modal__request:hover {
  background: #eadab9;
  transform: translateY(-1px);
}

.confirm-phone-modal__request--secondary {
  min-height: auto;
  margin-top: 18px;
  background: transparent;
  color: #8d6b37;
  letter-spacing: 0;
  text-decoration: underline;
  text-transform: none;
  text-underline-offset: 4px;
}

.confirm-phone-modal__request--secondary:hover {
  background: transparent;
}

.confirm-phone-modal__timer {
  margin-top: 18px;
  color: #6c6459;
  font-size: 14px;
  user-select: none;
}

.confirm-phone-modal__errors {
  margin: 18px 0 0;
  border-radius: 20px;
  background: #fff1f1;
  color: #a11d1d;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  padding: 14px 16px;
  text-align: left;
  white-space: pre-wrap;
}

@media (max-width: 640px) {
  .confirm-phone-modal {
    padding: 12px;
  }

  .confirm-phone-modal__panel {
    border-radius: 22px;
  }

  .confirm-phone-modal__header {
    padding: 24px 56px 0 20px;
  }

  .confirm-phone-modal__title {
    font-size: 25px;
  }

  .confirm-phone-modal__body {
    padding: 22px 20px 24px;
  }
}
</style>
